import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { useAdmin } from './AdminContext';
import { useConnectionMonitor } from './ConnectionMonitorContext';
import { notesApi } from '../services/cloudApi';
import { normalizeTimestamp } from '../utils/timestampUtils';
import { getLimitsForMembership, MEMBERSHIP_TYPES, UPGRADE_MESSAGES } from '../utils/membershipConstants';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  generateNoteId,
  loadOfflineQueue,
  enqueueOfflineNoteOp,
  removeOfflineNoteOp,
  markOfflineNoteOpFailure,
  getOfflineQueueCount,
  applyOfflineQueueOverlay,
} from '../utils/offlineNoteQueue';
import i18n from '../i18n';

const ORDER_STORAGE_PREFIX = 'notesplus_notes_order_';
const ORDER_DIRTY_PREFIX = 'notesplus_notes_order_dirty_';
const NOTE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const toCloudOrderIds = (ids) =>
  (Array.isArray(ids) ? ids : []).filter((id) => typeof id === 'string' && NOTE_ID_RE.test(id));

const resolveOrderDirtyKey = (userId) => {
  if (!userId) return null;
  return `${ORDER_DIRTY_PREFIX}user_${userId}`;
};

const isOrderDirty = (userId) => {
  if (typeof window === 'undefined') return false;
  const key = resolveOrderDirtyKey(userId);
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const setOrderDirty = (userId, dirty) => {
  if (typeof window === 'undefined') return;
  const key = resolveOrderDirtyKey(userId);
  if (!key) return;
  try {
    if (dirty) {
      window.localStorage.setItem(key, '1');
    } else {
      window.localStorage.removeItem(key);
    }
  } catch (error) {
    console.warn('[NotesContext] Failed to persist note order dirty flag:', error);
  }
};

const fetchCloudNoteOrder = async (token) => {
  try {
    const response = await notesApi.getOrder({ token });
    if (response && Object.prototype.hasOwnProperty.call(response, 'noteOrder')) {
      const { noteOrder } = response;
      if (noteOrder === null || Array.isArray(noteOrder)) {
        return { ok: true, noteOrder };
      }
    }
    return { ok: false };
  } catch (error) {
    console.warn('[NotesContext] Cloud note order fetch failed:', error);
    return { ok: false };
  }
};

/**
 * Ortak çalışma notu mu? Sunucudan gelen canonical alan: `isCollaborative`
 * (owner + >1 collaborator, ya da myRole === 'editor'). Tahmini alan üretilmez.
 */
const isSharedNote = (note) => !!(note?.isCollaborative || note?.myRole === 'editor');

/** cloudApi'nin ürettiği bağlantı hatası mı? (timeout/unreachable) */
const isNetworkFailure = (error) =>
  error?.isOffline === true ||
  error?.code === 'NETWORK_UNREACHABLE' ||
  error?.code === 'NETWORK_TIMEOUT' ||
  error?.code === 'LOCAL_SERVER_NOT_RUNNING';

const dispatchAppEvent = (name, detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, detail ? { detail } : undefined));
};

// NOT: `updateNoteInternal` içinde `options.showToast` boolean'ı bu adı gölgeler —
// modül helper'ı bilinçli olarak `emitToast` adını taşır.
const emitToast = (type, title, message) => {
  dispatchAppEvent('notesplus-show-toast', { type, title, message });
};

// Aynı not için eşzamanlı PUT isteklerini serileştir (flush + autosave çakışması)
const noteSaveTail = new Map();

const runSerializedNoteSave = async (noteId, task) => {
  const prev = noteSaveTail.get(noteId) || Promise.resolve();
  const run = prev.catch(() => {}).then(() => task());
  noteSaveTail.set(noteId, run);
  try {
    return await run;
  } finally {
    if (noteSaveTail.get(noteId) === run) {
      noteSaveTail.delete(noteId);
    }
  }
};

const resolveOrderStorageKey = (isAuthenticated, userId) => {
  if (isAuthenticated && userId) {
    return `${ORDER_STORAGE_PREFIX}user_${userId}`;
  }
  return `${ORDER_STORAGE_PREFIX}guest`;
};

const loadNotesOrder = (storageKey) => {
  if (typeof window === 'undefined' || !storageKey) {
    return [];
  }

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return parsed.filter((id) => typeof id === 'string');
    }
  } catch (error) {
    console.error('Error reading note order from localStorage:', error);
  }

  return [];
};

const persistNotesOrder = (storageKey, order) => {
  if (typeof window === 'undefined' || !storageKey) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(order));
  } catch (error) {
    console.error('Error saving note order to localStorage:', error);
  }
};

const applyOrderToNotes = (notesList = [], order = []) => {
  if (!Array.isArray(order) || order.length === 0) {
    return [...notesList];
  }

  const orderMap = new Map(order.map((id, index) => [id, index]));
  const fallbackIndex = orderMap.size + notesList.length;

  return [...notesList].sort((a, b) => {
    const indexA = orderMap.has(a.id) ? orderMap.get(a.id) : fallbackIndex;
    const indexB = orderMap.has(b.id) ? orderMap.get(b.id) : fallbackIndex;

    if (indexA !== indexB) {
      return indexA - indexB;
    }

    const createdA = new Date(a.createdAt || 0).getTime();
    const createdB = new Date(b.createdAt || 0).getTime();
    return createdA - createdB;
  });
};

const NotesContext = createContext(null);

// Kullanıcının üyelik tipini belirleyen helper fonksiyon
const getMembershipType = (user) => {
  if (!user) return MEMBERSHIP_TYPES.FREE;
  
  // Admin kontrolü
  if (
    user.role === 'admin' ||
    user.isAdmin === true ||
    user.isAdmin === 1 ||
    user.membershipType === 'admin' ||
    user.membership_type === 'admin'
  ) {
    return MEMBERSHIP_TYPES.ADMIN;
  }
  
  // Plus kontrolü — backend /me returns effective role; defensive expiry check for stale cache
  if (
    user.role === 'plus' ||
    user.membershipType === 'plus' ||
    user.membership_type === 'plus'
  ) {
    const expiryRaw = user.plusExpiryDate || user.plus_expiry_date;
    if (expiryRaw) {
      const exp = new Date(expiryRaw);
      if (!Number.isNaN(exp.getTime()) && exp.getTime() <= Date.now()) {
        return MEMBERSHIP_TYPES.FREE;
      }
    }
    return MEMBERSHIP_TYPES.PLUS;
  }
  
  return MEMBERSHIP_TYPES.FREE;
};

export const NotesProvider = ({ children }) => {
  const { t } = useTranslation('notes');
  const [notes, setNotes] = useState([]);
  const [noteOrder, setNoteOrder] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAuthenticated, authToken } = useAuth();
  const { getActiveToken, getActiveUserId, getActiveUser } = useAdmin();
  const { isServerOnline } = useConnectionMonitor();

  // Çevrimdışı senkron durumu (banner + editör göstergesi için)
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);
  const [offlineSyncState, setOfflineSyncState] = useState('idle'); // idle | syncing | error
  const offlineFlushRef = useRef(false);
  const flushSkipReloadRef = useRef(false);
  const flushOfflineQueueRef = useRef(null);
  const notesLoadGenRef = useRef(0);
  const notesRef = useRef(notes);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  const reorderSaveTailRef = useRef(Promise.resolve());
  const reorderSaveMetaRef = useRef({});

  /** Sunucu olmadan hiç yapılamayan işlemler (silme, paylaşım, ortak çalışma) */
  const assertServerRequiredAction = useCallback(() => {
    if (isServerOnline) return true;
    emitToast(
      'warning',
      i18n.t('notes:editor.offlineToast.title'),
      i18n.t('shared:readOnlyMode.requiresConnection')
    );
    return false;
  }, [isServerOnline]);
  
  // Impersonate durumunda impersonate token'ı, değilse normal token'ı kullan
  const activeToken = getActiveToken ? getActiveToken() : authToken;
  const activeUserId = getActiveUserId ? getActiveUserId() : user?.id;
  const activeUser = getActiveUser ? getActiveUser() : user;
  const orderStorageKey = useMemo(
    () => resolveOrderStorageKey(isAuthenticated, activeUserId),
    [isAuthenticated, activeUserId]
  );

  reorderSaveMetaRef.current = {
    token: activeToken,
    userId: activeUserId,
    online: isServerOnline,
    isAuth: isAuthenticated,
  };

  const loadStoredOrder = useCallback(() => loadNotesOrder(orderStorageKey), [orderStorageKey]);

  const enqueueReorderSave = useCallback((noteIds) => {
    const run = async () => {
      const { token, userId, online, isAuth } = reorderSaveMetaRef.current;
      if (!isAuth || !token || !userId) return;
      const payload = toCloudOrderIds(noteIds);
      if (!online) {
        setOrderDirty(userId, true);
        return;
      }
      try {
        await notesApi.reorder({ token, noteIds: payload });
        setOrderDirty(userId, false);
      } catch (error) {
        console.warn('[NotesContext] Note order cloud save failed:', error);
        setOrderDirty(userId, true);
      }
    };
    reorderSaveTailRef.current = reorderSaveTailRef.current.catch(() => {}).then(run);
    return reorderSaveTailRef.current;
  }, []);

  const flushPendingNoteOrder = useCallback(() => {
    const { token, userId, online, isAuth } = reorderSaveMetaRef.current;
    if (!isAuth || !token || !userId || !online) return Promise.resolve();
    if (!isOrderDirty(userId)) return Promise.resolve();
    const stored = loadNotesOrder(resolveOrderStorageKey(true, userId));
    const notesList = notesRef.current || [];
    const applied = applyOrderToNotes(notesList, stored);
    const ids = toCloudOrderIds(applied.map((note) => note.id));
    const payload = ids.length ? ids : toCloudOrderIds(stored);
    return enqueueReorderSave(payload);
  }, [enqueueReorderSave]);

  const persistOrder = useCallback(
    (order) => {
      if (!Array.isArray(order)) {
        return;
      }
      persistNotesOrder(orderStorageKey, order);
    },
    [orderStorageKey]
  );

  const syncNotesWithOrder = useCallback(
    (notesList, order) => {
      const applied = applyOrderToNotes(notesList, order);
      const finalOrder = applied.map((note) => note.id);
      persistOrder(finalOrder);
      setNoteOrder(finalOrder);
      return applied;
    },
    [persistOrder]
  );

  const applyNotesWithResolvedOrder = useCallback(
    async (notesList, orderFetch) => {
      const storedOrder = loadStoredOrder();

      // Pending local reorder not yet on server — keep local, then push
      if (activeUserId && isOrderDirty(activeUserId)) {
        const applied = syncNotesWithOrder(notesList, storedOrder);
        enqueueReorderSave(applied.map((note) => note.id));
        return applied;
      }

      if (orderFetch?.ok === true && Array.isArray(orderFetch.noteOrder)) {
        return syncNotesWithOrder(notesList, orderFetch.noteOrder);
      }

      if (orderFetch?.ok === true && orderFetch.noteOrder === null) {
        const baseline = storedOrder.length ? storedOrder : notesList.map((note) => note.id);
        const applied = syncNotesWithOrder(notesList, baseline);
        enqueueReorderSave(applied.map((note) => note.id));
        return applied;
      }

      return syncNotesWithOrder(notesList, storedOrder);
    },
    [activeUserId, loadStoredOrder, syncNotesWithOrder, enqueueReorderSave]
  );

  // UI + localStorage immediately; cloud PATCH serialized, never rolls back UI
  const reorderNotes = useCallback(
    (updater) => {
      let finalOrder = [];
      setNotes((prevNotes) => {
        const currentOrder = prevNotes.map((note) => note.id);
        const desiredOrder = typeof updater === 'function' ? updater(currentOrder) : updater;

        const uniqueOrder = Array.isArray(desiredOrder)
          ? desiredOrder.filter((id, index, arr) => typeof id === 'string' && arr.indexOf(id) === index)
          : [];

        const noteMap = new Map(prevNotes.map((note) => [note.id, note]));
        const reordered = [];

        uniqueOrder.forEach((id) => {
          const note = noteMap.get(id);
          if (note) {
            reordered.push(note);
            noteMap.delete(id);
          }
        });

        noteMap.forEach((note) => {
          reordered.push(note);
        });

        finalOrder = reordered.map((note) => note.id);

        return reordered;
      });

      persistOrder(finalOrder);
      setNoteOrder(finalOrder);
      if (isAuthenticated && activeUserId) {
        enqueueReorderSave(finalOrder);
      }
    },
    [persistOrder, enqueueReorderSave, isAuthenticated, activeUserId]
  );

  const moveNoteUp = useCallback(
    (noteId) => {
      reorderNotes((currentOrder) => {
        const index = currentOrder.indexOf(noteId);
        if (index <= 0) {
          return currentOrder;
        }
        const updated = [...currentOrder];
        const [item] = updated.splice(index, 1);
        updated.splice(index - 1, 0, item);
        return updated;
      });
    },
    [reorderNotes]
  );

  const moveNoteDown = useCallback(
    (noteId) => {
      reorderNotes((currentOrder) => {
        const index = currentOrder.indexOf(noteId);
        if (index === -1 || index >= currentOrder.length - 1) {
          return currentOrder;
        }
        const updated = [...currentOrder];
        const [item] = updated.splice(index, 1);
        updated.splice(index + 1, 0, item);
        return updated;
      });
    },
    [reorderNotes]
  );

  // İlk yükleme flag'i - sadece bir kez çalışsın
  const hasLoadedRef = React.useRef(false);
  const lastUserIdRef = React.useRef(null);

  // Kullanıcı giriş yaptığında flag'leri sıfırla
  useEffect(() => {
    const handleUserLogin = () => {
      hasLoadedRef.current = false;
      lastUserIdRef.current = null;
    };

    window.addEventListener('notesplus-user-logged-in', handleUserLogin);
    return () => {
      window.removeEventListener('notesplus-user-logged-in', handleUserLogin);
    };
  }, []);

  // Logout olduğunda tüm not state'ini temizle
  useEffect(() => {
    const handleLogout = () => {
      setSelectedNote(null);
      setNotes([]);
      setNoteOrder([]);
    };

    window.addEventListener('notesplus-user-logged-out', handleLogout);
    return () => {
      window.removeEventListener('notesplus-user-logged-out', handleLogout);
    };
  }, []);

  // Verileri yükle (sadece ilk yüklemede ve user değiştiğinde)
  useEffect(() => {
    // Token yoksa veri yükleme
    if (!activeToken || !isAuthenticated || !activeUserId) {
      setNotes([]);
      setNoteOrder([]);
      setIsLoading(false);
      return;
    }
    
    const currentUserId = activeUserId;
    
    // Eğer aynı kullanıcı ve zaten yüklendiyse, tekrar yükleme
    if (hasLoadedRef.current && lastUserIdRef.current === currentUserId) {
      return;
    }

    const loadNotes = async () => {
      const loadGen = ++notesLoadGenRef.current;
      setIsLoading(true);

      try {
        // Cold-start: kuyruk varsa GET'ten ÖNCE UI'ya hydrate et (kullanıcı boş/eski liste görmesin)
        const pendingOps = loadOfflineQueue(currentUserId);
        if (pendingOps.length) {
          const storedOrder = loadStoredOrder();
          const hydrated = applyOfflineQueueOverlay([], pendingOps);
          if (loadGen !== notesLoadGenRef.current) return;
          setNotes(syncNotesWithOrder(hydrated, storedOrder));
          setOfflineQueueCount(pendingOps.length);

          // Backend online ise önce kuyruğu yaz, sonra listeyi çek
          if (isServerOnline && flushOfflineQueueRef.current) {
            flushSkipReloadRef.current = true;
            try {
              await flushOfflineQueueRef.current();
            } finally {
              flushSkipReloadRef.current = false;
            }
          }
        }

        if (loadGen !== notesLoadGenRef.current) return;

        // API'den notları ve cloud sırayı paralel çek
        const [response, orderFetch] = await Promise.all([
          notesApi.list({ token: activeToken }),
          fetchCloudNoteOrder(activeToken),
        ]);
        if (loadGen !== notesLoadGenRef.current) return;
        const apiNotes = response?.notes || [];
        
        // Convert to display format (include collaborative fields from API)
        const formattedNotes = apiNotes.map(note => ({
          id: note.id,
          title: note.title || t('common:unnamed'),
          content: note.content || '',
          titleColor: note.titleColor || note.title_color || '#ffffff',
          createdAt: normalizeTimestamp(note.createdAt || note.created_at),
          updatedAt: normalizeTimestamp(note.updatedAt || note.updated_at || note.createdAt || note.created_at),
          isCollaborative: !!note.isCollaborative,
          myRole: note.myRole || null,
          collaborators: Array.isArray(note.collaborators) ? note.collaborators : [],
          categories: Array.isArray(note.categories) && note.categories.length > 0
            ? note.categories
            : (note.categoryName || note.category_name)
              ? [{ name: note.categoryName || note.category_name, color: note.categoryColor || note.category_color || '#3b82f6' }]
              : [],
        }));
        // Dedupe by id so React keys are unique (avoid duplicate key warning)
        const seenIds = new Set();
        const uniqueNotes = formattedNotes.filter((n) => {
          if (seenIds.has(n.id)) return false;
          seenIds.add(n.id);
          return true;
        });

        // Notlardaki kategorileri NavSidebar ile senkronize et
        const uniqueCats = [];
        const seenCatNames = new Set();
        for (const n of uniqueNotes) {
          for (const cat of (n.categories || [])) {
            if (cat.name && !seenCatNames.has(cat.name)) {
              seenCatNames.add(cat.name);
              uniqueCats.push(cat);
            }
          }
        }
        if (uniqueCats.length > 0) {
          window.dispatchEvent(new CustomEvent('notesplus-sync-categories', { detail: { categories: uniqueCats } }));
        }

        const remainingOps = loadOfflineQueue(currentUserId);
        const mergedNotes = applyOfflineQueueOverlay(uniqueNotes, remainingOps);
        if (loadGen !== notesLoadGenRef.current) return;
        const orderedNotes = await applyNotesWithResolvedOrder(mergedNotes, orderFetch);
        if (loadGen !== notesLoadGenRef.current) return;
        setNotes(orderedNotes);
        setOfflineQueueCount(remainingOps.length);
        if (remainingOps.length) {
          setOfflineSyncState('error');
        }
        hasLoadedRef.current = true;
        lastUserIdRef.current = currentUserId;
      } catch (error) {
        console.error('Error loading notes:', error);
        if (loadGen !== notesLoadGenRef.current) return;
        // GET başarısız: sunucu snapshot yok — kuyruk + eldeki yerel liste korunur, asla boşaltılmaz
        const queuedOps = loadOfflineQueue(currentUserId);
        const mergedNotes = applyOfflineQueueOverlay(notesRef.current || [], queuedOps);
        if (mergedNotes.length) {
          const storedOrder = loadStoredOrder();
          setNotes(syncNotesWithOrder(mergedNotes, storedOrder));
        }
        setOfflineQueueCount(queuedOps.length);
        hasLoadedRef.current = true;
        lastUserIdRef.current = currentUserId;
      } finally {
        if (loadGen === notesLoadGenRef.current) {
          setIsLoading(false);
        }
      }
    };

    // Kısa bir gecikme ile kullanıcının tam olarak set edildiğinden emin ol
    const timer = setTimeout(() => {
      loadNotes();
    }, 100);

    return () => clearTimeout(timer);
  }, [activeUserId, isAuthenticated, activeToken, activeUser, isServerOnline, loadStoredOrder, syncNotesWithOrder, applyNotesWithResolvedOrder]);


  const importNotes = useCallback(
    async (rawNotes = [], { onProgress } = {}) => {
      const isProgressFn = typeof onProgress === 'function';

      const normalizedList = (Array.isArray(rawNotes) ? rawNotes : []).map((note) => {
        const id =
          typeof note?.id === 'string' && note.id
            ? note.id
            : `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const createdAt = normalizeTimestamp(note?.createdAt || note?.created_at);
        const updatedAt = normalizeTimestamp(
          note?.updatedAt || note?.updated_at || note?.createdAt || note?.created_at
        );

        return {
          id,
          title: typeof note?.title === 'string' ? note.title : '',
          content: typeof note?.content === 'string' ? note.content : '',
          titleColor: note?.titleColor || note?.title_color || '#ffffff',
          createdAt,
          updatedAt,
        };
      });

      const storedOrder = loadStoredOrder();
      const appliedList = syncNotesWithOrder(normalizedList, storedOrder);

      // CRITICAL: Write directly to database (API) first when authenticated, then update state
      if (isAuthenticated && activeToken && activeUserId) {
        const failedNotes = [];
        const total = appliedList.length || 1;
        let processed = 0;

        for (const note of appliedList) {
          try {
            await notesApi.upsert({
              token: activeToken,
              notes: [{
                id: note.id,
                title: note.title || t('common:unnamed'),
                content: note.content || '',
                titleColor: note.titleColor || '#ffffff',
                createdAt: note.createdAt,
                updatedAt: note.updatedAt,
              }],
            });
          } catch (error) {
            console.warn('Not içe aktarılamadı:', note.id, error);
            failedNotes.push({ note, error });
          }

          processed += 1;
          if (isProgressFn) {
            onProgress({ processed, total });
          }
        }

        // Only update UI state after database writes are done
        setNotes(appliedList);
        setSelectedNote((prevSelected) => {
          if (prevSelected && appliedList.some((note) => note.id === prevSelected.id)) {
            return prevSelected;
          }
          return appliedList[0] || null;
        });

        return {
          importedNotes: appliedList,
          failedNotes,
        };
      }

      // Guest/offline: no API, update state only
      setNotes(appliedList);
      setSelectedNote((prevSelected) => {
        if (prevSelected && appliedList.some((note) => note.id === prevSelected.id)) {
          return prevSelected;
        }
        return appliedList[0] || null;
      });

      if (isProgressFn) {
        onProgress({ processed: appliedList.length, total: appliedList.length });
      }

      return {
        importedNotes: appliedList,
        failedNotes: [],
      };
    },
    [isAuthenticated, activeToken, activeUserId, loadStoredOrder, syncNotesWithOrder]
  );

  // Listen for reload event to force reload from API
  useEffect(() => {
    if (typeof window === 'undefined' || !isAuthenticated || !activeToken || !activeUserId) {
      return undefined;
    }

    const handleReloadNotes = async () => {
      const loadGen = ++notesLoadGenRef.current;
      setIsLoading(true);
      try {
        const [response, orderFetch] = await Promise.all([
          notesApi.list({ token: activeToken }),
          fetchCloudNoteOrder(activeToken),
        ]);
        if (loadGen !== notesLoadGenRef.current) return;
        const apiNotes = response?.notes || [];
        
        const formattedNotes = apiNotes.map(note => ({
          id: note.id,
          title: note.title || t('common:unnamed'),
          content: note.content || '',
          titleColor: note.titleColor || note.title_color || '#ffffff',
          createdAt: normalizeTimestamp(note.createdAt || note.created_at),
          updatedAt: normalizeTimestamp(note.updatedAt || note.updated_at || note.createdAt || note.created_at),
          isCollaborative: !!note.isCollaborative,
          myRole: note.myRole || null,
          collaborators: Array.isArray(note.collaborators) ? note.collaborators : [],
          categories: Array.isArray(note.categories) && note.categories.length > 0
            ? note.categories
            : (note.categoryName || note.category_name)
              ? [{ name: note.categoryName || note.category_name, color: note.categoryColor || note.category_color || '#3b82f6' }]
              : [],
        }));
        const seenIdsReload = new Set();
        const uniqueNotesReload = formattedNotes.filter((n) => {
          if (seenIdsReload.has(n.id)) return false;
          seenIdsReload.add(n.id);
          return true;
        });
        const remainingOps = loadOfflineQueue(activeUserId);
        const mergedNotes = applyOfflineQueueOverlay(uniqueNotesReload, remainingOps);
        if (remainingOps.length) {
          setOfflineSyncState('error');
        }
        const orderedNotes = await applyNotesWithResolvedOrder(mergedNotes, orderFetch);
        if (loadGen !== notesLoadGenRef.current) return;
        setNotes(orderedNotes);
        setOfflineQueueCount(remainingOps.length);
        
        if (formattedNotes.length === 0) {
          console.warn('⚠️ [NotesContext] No notes found in PostgreSQL. Make sure notes exist before importing backup.');
        }
      } catch (error) {
        if (loadGen !== notesLoadGenRef.current) return;
        console.error('❌ [NotesContext] Failed to reload notes:', error);
        console.error('❌ [NotesContext] Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response,
        });
        const queuedOps = loadOfflineQueue(activeUserId);
        const mergedNotes = applyOfflineQueueOverlay(notesRef.current || [], queuedOps);
        if (mergedNotes.length) {
          const storedOrder = loadStoredOrder();
          setNotes(syncNotesWithOrder(mergedNotes, storedOrder));
        }
      } finally {
        if (loadGen === notesLoadGenRef.current) {
          setIsLoading(false);
        }
      }
    };

    window.addEventListener('notesplus-reload-notes', handleReloadNotes);
    return () => {
      window.removeEventListener('notesplus-reload-notes', handleReloadNotes);
    };
  }, [isAuthenticated, activeToken, activeUserId, loadStoredOrder, syncNotesWithOrder, applyNotesWithResolvedOrder]);

  // Ortak not güncellendi (diğer kullanıcı Kaydet dedi) – sadece o notu güncelle, sayfa yenileme yok
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleNoteUpdated = (event) => {
      const { noteId, note: incoming } = event?.detail || {};
      if (!noteId || !incoming) return;
      const incomingUpdatedAt = normalizeTimestamp(incoming.updatedAt || incoming.updated_at);
      const updated = {
        id: incoming.id || noteId,
        title: incoming.title || t('common:unnamed'),
        content: incoming.content ?? '',
        titleColor: incoming.titleColor || incoming.title_color || '#ffffff',
        createdAt: normalizeTimestamp(incoming.createdAt || incoming.created_at),
        updatedAt: incomingUpdatedAt,
      };
      // Kategoriler socket payload'ında geliyorsa güncelle
      if (Array.isArray(incoming.categories)) {
        updated.categories = incoming.categories;
      }
      setNotes((prevNotes) =>
        prevNotes.map((n) => {
          if (n.id !== noteId) return n;
          // Gelen güncelleme yerel kayıttan 3 saniyeden fazla eskiyse yoksay (saat farkı toleransı)
          const localUpdatedAt = normalizeTimestamp(n.updatedAt);
          if (localUpdatedAt && incomingUpdatedAt && incomingUpdatedAt < localUpdatedAt - 3000) return n;
          return {
            ...n,
            ...updated,
            // categories: updated.categories varsa kullan, yoksa mevcut değeri koru
            categories: updated.categories !== undefined ? updated.categories : n.categories,
            isCollaborative: n.isCollaborative,
            myRole: n.myRole,
            collaborators: n.collaborators || [],
          };
        })
      );
      setSelectedNote((current) => {
        if (!current || current.id !== noteId) return current;
        // Gelen güncelleme yerel kayıttan 3 saniyeden fazla eskiyse yoksay
        const localUpdatedAt = normalizeTimestamp(current.updatedAt);
        if (localUpdatedAt && incomingUpdatedAt && incomingUpdatedAt < localUpdatedAt - 3000) return current;
        return {
          ...current,
          ...updated,
          categories: updated.categories !== undefined ? updated.categories : current.categories,
          isCollaborative: current.isCollaborative,
          myRole: current.myRole,
          collaborators: current.collaborators || [],
        };
      });
    };
    window.addEventListener('notesplus-note-updated', handleNoteUpdated);
    return () => window.removeEventListener('notesplus-note-updated', handleNoteUpdated);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleRemoteNotes = (event) => {
      const { detail } = event || {};
      if (!detail || detail.entity !== 'notes' || !Array.isArray(detail.records) || !detail.records.length) {
        return;
      }

      const normalizedRecords = detail.records.map((note) => ({
        id: note.id,
        title: note.title || t('common:unnamed'),
        content: note.content || '',
        titleColor: note.titleColor || note.title_color || '#ffffff',
        createdAt: normalizeTimestamp(note.createdAt || note.created_at),
        updatedAt: normalizeTimestamp(note.updatedAt || note.updated_at),
      }));

      let mergedOrder = [];
      setNotes((prevNotes) => {
        const overlayOps = loadOfflineQueue(activeUserId);

        // Eğer önceki veri yoksa, remote + kuyruk overlay
        if (!prevNotes || prevNotes.length === 0) {
          const overlaid = applyOfflineQueueOverlay(normalizedRecords, overlayOps);
          mergedOrder = overlaid.map((note) => note.id);
          return overlaid;
        }
        
        const mergedMap = new Map();
        // Önce mevcut verileri ekle - MEVCUT VERİLERİ KORU
        prevNotes.forEach((note) => mergedMap.set(note.id, note));

        normalizedRecords.forEach((incoming) => {
          const existing = mergedMap.get(incoming.id);
          if (!existing) {
            // Yeni kayıt, ekle
            mergedMap.set(incoming.id, incoming);
            return;
          }

          // CRITICAL: Check if local record is deleted (deleted_at takes priority)
          const existingDeleted = existing.deletedAt || existing.deleted_at;
          const existingDeletedTs = existingDeleted ? 
            (typeof existingDeleted === 'number' ? existingDeleted : new Date(existingDeleted).getTime() / 1000) : 0;

          // RULE 1: If local record is deleted, keep it (pending delete will be pushed)
          if (existingDeletedTs > 0) {
            // Local record is deleted - keep it, don't accept server record
            return;
          }

          // RULE 2: Timestamp comparison for updates
          const existingUpdatedAt = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
          const incomingUpdatedAt = new Date(incoming.updatedAt || incoming.createdAt || 0).getTime();

          // Only update if server record is newer
          if (incomingUpdatedAt > existingUpdatedAt) {
            mergedMap.set(incoming.id, incoming);
          }
          // If equal or local is newer, keep existing (no change)
        });

        const mergedList = [];
        const existingOrder = noteOrder.length ? noteOrder : prevNotes.map((note) => note.id);

        existingOrder.forEach((id) => {
          const item = mergedMap.get(id);
          if (item) {
            mergedList.push(item);
            mergedMap.delete(id);
          }
        });

        mergedMap.forEach((note) => {
          mergedList.push(note);
        });

        // Pending kişisel kuyruk remote snapshot tarafından ezilmesin
        const overlaid = applyOfflineQueueOverlay(mergedList, overlayOps);
        mergedOrder = overlaid.map((note) => note.id);
        return overlaid;
      });

      if (mergedOrder.length) {
        persistOrder(mergedOrder);
        setNoteOrder(mergedOrder);
      }
    };

    window.addEventListener('notesplus-sync-remote', handleRemoteNotes);

    return () => {
      window.removeEventListener('notesplus-sync-remote', handleRemoteNotes);
    };
  }, [isAuthenticated, activeUser, activeToken, activeUserId, noteOrder, persistOrder]);

  // ─── Çevrimdışı kişisel not yardımcıları ───────────────────────────────────
  const refreshOfflineQueueCount = useCallback(() => {
    setOfflineQueueCount(getOfflineQueueCount(activeUserId));
  }, [activeUserId]);

  /** Kişisel notu yerel state'e ekler + offline kuyruğa `create` olarak yazar */
  const addLocalNoteWithQueue = useCallback((note) => {
    let nextOrder = [];
    setNotes((prevNotes) => {
      const updatedNotes = [...prevNotes, note];
      nextOrder = updatedNotes.map((n) => n.id);
      return updatedNotes;
    });
    persistOrder(nextOrder);
    setNoteOrder(nextOrder);

    enqueueOfflineNoteOp(activeUserId, {
      noteId: note.id,
      type: 'create',
      title: note.title || '',
      content: note.content || '',
      titleColor: note.titleColor || '#ffffff',
      categories: note.categories,
      createdAt: note.createdAt,
      updatedAt: note.updatedAt,
    });
    refreshOfflineQueueCount();
    return note;
  }, [activeUserId, persistOrder, refreshOfflineQueueCount]);

  // Yeni not oluştur
  // 🔴 SENKRON MİMARİ: PostgreSQL başarılı olmadan UI değişmeyecek
  // Kural: DB yoksa UI da yok
  const createNote = async () => {
    try {
      // Üyelik limiti kontrolü
      const membershipType = getMembershipType(activeUser);
      const limits = getLimitsForMembership(membershipType);
      const currentNoteCount = notes.length;
      
      if (limits.maxNoteCount !== Infinity && currentNoteCount >= limits.maxNoteCount) {
        // Limit aşıldı - hata döndür
        const error = new Error(UPGRADE_MESSAGES.noteLimit);
        error.code = 'NOTE_LIMIT_EXCEEDED';
        error.limitInfo = {
          current: currentNoteCount,
          max: limits.maxNoteCount,
          membershipType,
        };
        throw error;
      }
      
      // Yeni not objesi oluştur — client UUID: sunucu geçerli UUID'yi koruduğu için
      // aynı not iki kez upsert edilse bile kopya oluşmaz (idempotent sync).
      const now = new Date().toISOString();
      const tempNote = {
        id: generateNoteId(),
        title: '',
        content: '',
        titleColor: '#ffffff',
        createdAt: now,
        updatedAt: now,
      };

      // Çevrimdışı: kişisel notu yerel olarak oluştur ve kuyruğa al
      if (!isServerOnline && isAuthenticated && activeUserId) {
        return addLocalNoteWithQueue(tempNote);
      }

      // 🔴 KRİTİK: Önce API'ye kaydet - PostgreSQL başarılı olmadan UI değişmeyecek
      if (isAuthenticated && activeToken && activeUserId) {
        try {
          // API çağrısı - başarılı olursa DB'den dönen gerçek not ile state'e ekle
          const response = await notesApi.upsert({
            token: activeToken,
            notes: [{
              id: tempNote.id,
              title: tempNote.title || t('common:unnamed'),
              content: tempNote.content || '',
              titleColor: tempNote.titleColor || '#ffffff',
              createdAt: tempNote.createdAt,
              updatedAt: tempNote.updatedAt,
            }],
          });
          
          // API başarılı - DB'den dönen gerçek notu al
          const savedNote = response?.note || response?.notes?.[0];
          if (!savedNote) {
            throw new Error(t('errors.invalidApiResponse'));
          }
          
          // DB'den dönen gerçek ID ve verilerle notu formatla
          const newNote = {
            id: savedNote.id,
            title: savedNote.title || t('common:unnamed'),
            content: savedNote.content || '',
            titleColor: savedNote.titleColor || savedNote.title_color || '#ffffff',
            createdAt: normalizeTimestamp(savedNote.createdAt || savedNote.created_at),
            updatedAt: normalizeTimestamp(savedNote.updatedAt || savedNote.updated_at || savedNote.createdAt || savedNote.created_at),
          };
          
          // ✅ Başarılı - Şimdi state'e ekle (DB'den dönen gerçek veri ile)
          let nextOrder = [];
          setNotes(prevNotes => {
            const updatedNotes = [...prevNotes, newNote];
            nextOrder = updatedNotes.map((note) => note.id);
            return updatedNotes;
          });
          persistOrder(nextOrder);
          setNoteOrder(nextOrder);
          
          return newNote;
        } catch (error) {
          console.error('Error saving note to API:', error);

          // Bağlantı tam bu anda gittiyse: kişisel notu kaybetme — yerel oluştur + kuyruğa al
          if (isNetworkFailure(error)) {
            const localNote = addLocalNoteWithQueue(tempNote);
            emitToast(
              'info',
              t('editor.offlineToast.title'),
              t('editor.offlineToast.message')
            );
            return localNote;
          }

          // Kullanıcıya net hata mesajı göster
          const errorMessage = getApiErrorMessage(error, { fallback: t('errors.createFailedGeneric') });

          // Toast bildirimi göster
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'error',
                title: t('errors.createFailedTitle'),
                message: errorMessage,
              },
            }));
          }

          // State'e dokunmadık - DB yoksa UI da yok
          return null;
        }
      } else {
        // Kullanıcı giriş yapmamış - sadece local state'e ekle (offline mode)
        // Not: Bu durumda da senkron mantığı korunur - local storage'a kaydedilebilir
        let nextOrder = [];
        setNotes(prevNotes => {
          const updatedNotes = [...prevNotes, tempNote];
          nextOrder = updatedNotes.map((note) => note.id);
          return updatedNotes;
        });
        persistOrder(nextOrder);
        setNoteOrder(nextOrder);
        return tempNote;
      }
    } catch (error) {
      console.error('Error creating note:', error);

      // Kullanıcıya hata mesajı göster
      const errorMessage = error.code === 'NOTE_LIMIT_EXCEEDED'
        ? error.message
        : t('errors.createFailedWithReason', { reason: getApiErrorMessage(error, { fallback: t('errors.unknownError') }) });

      // Toast bildirimi göster
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('errors.createFailedTitle'),
            message: errorMessage,
          },
        }));
      }

      return null;
    }
  };

  // Not çoğalt
  // 🔴 SENKRON MİMARİ: PostgreSQL başarılı olmadan UI değişmeyecek
  // createNote ile aynı mantık - önce API, sonra UI
  const duplicateNote = async (noteId) => {
    try {
      const originalNote = notes.find((note) => note.id === noteId);
      if (!originalNote) {
        // Orijinal not bulunamadı
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'error',
              title: t('errors.duplicateFailedTitle'),
              message: t('errors.duplicateOriginalNotFound'),
            },
          }));
        }
        return null;
      }

      // Üyelik limiti kontrolü
      const membershipType = getMembershipType(activeUser);
      const limits = getLimitsForMembership(membershipType);
      const currentNoteCount = notes.length;
      
      if (limits.maxNoteCount !== Infinity && currentNoteCount >= limits.maxNoteCount) {
        // Limit aşıldı - hata döndür
        const error = new Error(UPGRADE_MESSAGES.noteLimit);
        error.code = 'NOTE_LIMIT_EXCEEDED';
        error.limitInfo = {
          current: currentNoteCount,
          max: limits.maxNoteCount,
          membershipType,
        };
        throw error;
      }

      const now = new Date().toISOString();
      const tempDuplicatedNote = {
        ...originalNote,
        id: generateNoteId(),
        // Kopya her zaman kişisel nottur — ortak çalışma alanları taşınmaz
        isCollaborative: false,
        myRole: null,
        collaborators: [],
        createdAt: now,
        updatedAt: now,
      };

      // Çevrimdışı: kopyayı yerel oluştur + kuyruğa al
      if (!isServerOnline && isAuthenticated && activeUserId) {
        return addLocalNoteWithQueue(tempDuplicatedNote);
      }

      // 🔴 KRİTİK: Önce API'ye kaydet - başarılı olursa DB'den dönen gerçek not ile state'e ekle
      if (isAuthenticated && activeToken && activeUserId) {
        try {
          // API çağrısı - başarılı olursa DB'den dönen gerçek not ile state'e ekle
          const response = await notesApi.upsert({
            token: activeToken,
            notes: [{
              id: tempDuplicatedNote.id,
              title: tempDuplicatedNote.title || t('common:unnamed'),
              content: tempDuplicatedNote.content || '',
              titleColor: tempDuplicatedNote.titleColor || '#ffffff',
              createdAt: tempDuplicatedNote.createdAt,
              updatedAt: tempDuplicatedNote.updatedAt,
            }],
          });
          
          // API başarılı - DB'den dönen gerçek notu al
          const savedNote = response?.note || response?.notes?.[0];
          if (!savedNote) {
            throw new Error(t('errors.invalidApiResponse'));
          }
          
          // DB'den dönen gerçek ID ve verilerle notu formatla
          const duplicatedNote = {
            id: savedNote.id,
            title: savedNote.title || t('common:unnamed'),
            content: savedNote.content || '',
            titleColor: savedNote.titleColor || savedNote.title_color || '#ffffff',
            createdAt: normalizeTimestamp(savedNote.createdAt || savedNote.created_at),
            updatedAt: normalizeTimestamp(savedNote.updatedAt || savedNote.updated_at || savedNote.createdAt || savedNote.created_at),
          };
          
          // ✅ Başarılı - Şimdi state'e ekle (DB'den dönen gerçek veri ile)
          let nextOrder = [];
          setNotes((prevNotes) => {
            const updatedNotes = [...prevNotes];
            const originalIndex = updatedNotes.findIndex((note) => note.id === noteId);
            const insertIndex = originalIndex >= 0 ? originalIndex + 1 : updatedNotes.length;
            updatedNotes.splice(insertIndex, 0, duplicatedNote);
            nextOrder = updatedNotes.map((note) => note.id);
            return updatedNotes;
          });
          persistOrder(nextOrder);
          setNoteOrder(nextOrder);
          
          return duplicatedNote;
        } catch (error) {
          console.error('Error duplicating note in API:', error);

          // Bağlantı o anda gittiyse kopyayı yerel tut + kuyruğa al
          if (isNetworkFailure(error)) {
            const localNote = addLocalNoteWithQueue(tempDuplicatedNote);
            emitToast('info', t('editor.offlineToast.title'), t('editor.offlineToast.message'));
            return localNote;
          }

          // Kullanıcıya net hata mesajı göster
          const errorMessage = getApiErrorMessage(error, { fallback: t('errors.duplicateFailedGeneric') });

          // Toast bildirimi göster
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'error',
                title: t('errors.duplicateFailedTitle'),
                message: errorMessage,
              },
            }));
          }

          // State'e dokunmadık - DB yoksa UI da yok
          return null;
        }
      } else {
        // Kullanıcı giriş yapmamış - sadece local state'e ekle (offline mode)
        let nextOrder = [];
        setNotes((prevNotes) => {
          const updatedNotes = [...prevNotes];
          const originalIndex = updatedNotes.findIndex((note) => note.id === noteId);
          const insertIndex = originalIndex >= 0 ? originalIndex + 1 : updatedNotes.length;
          updatedNotes.splice(insertIndex, 0, tempDuplicatedNote);
          nextOrder = updatedNotes.map((note) => note.id);
          return updatedNotes;
        });
        persistOrder(nextOrder);
        setNoteOrder(nextOrder);
        return tempDuplicatedNote;
      }
    } catch (error) {
      console.error('Error duplicating note:', error);

      // Kullanıcıya hata mesajı göster
      const errorMessage = error.code === 'NOTE_LIMIT_EXCEEDED'
        ? error.message
        : t('errors.duplicateFailedWithReason', { reason: getApiErrorMessage(error, { fallback: t('errors.unknownError') }) });

      // Toast bildirimi göster
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('errors.duplicateFailedTitle'),
            message: errorMessage,
          },
        }));
      }

      return null;
    }
  };

  // Notu güncelle
  // 🔴 SENKRON MİMARİ: UI sadece DB'nin doğruladığı veriyi gösterir
  // Rollback mekanizması: API başarısız olursa state eski haline döner
  /** Kişisel notu yerel state'e uygula + offline kuyruğa `update` yaz */
  const applyOfflineNoteUpdate = useCallback((updatedNote) => {
    const now = new Date().toISOString();
    const localNote = {
      ...updatedNote,
      updatedAt: now,
      createdAt: normalizeTimestamp(updatedNote.createdAt || updatedNote.created_at || now),
    };

    setNotes((prevNotes) =>
      prevNotes.map((note) => (note.id === localNote.id ? { ...note, ...localNote } : note))
    );
    setSelectedNote((current) => {
      if (!current || current.id !== localNote.id) return current;
      return { ...current, ...localNote };
    });

    enqueueOfflineNoteOp(activeUserId, {
      noteId: localNote.id,
      type: 'update',
      title: localNote.title || '',
      content: localNote.content || '',
      titleColor: localNote.titleColor || '#ffffff',
      categories: localNote.categories,
      createdAt: localNote.createdAt,
      updatedAt: localNote.updatedAt,
    });
    refreshOfflineQueueCount();
    return localNote;
  }, [activeUserId, refreshOfflineQueueCount]);

  const updateNote = async (updatedNote, options = {}) => {
    const noteId = updatedNote?.id;
    const knownNote = noteId ? notesRef.current.find((note) => note.id === noteId) : null;
    const shared = isSharedNote(updatedNote) || isSharedNote(knownNote);

    // Ortak çalışma notu: çevrimdışıyken kesinlikle salt okunur, kuyruğa da girmez
    if (!isServerOnline && shared) {
      emitToast(
        'warning',
        i18n.t('notes:editor.offlineToast.title'),
        i18n.t('shared:readOnlyMode.cannotModify')
      );
      const err = new Error(i18n.t('shared:readOnlyMode.cannotModify'));
      err.code = 'SHARED_NOTE_READ_ONLY';
      throw err;
    }

    // Kişisel not: çevrimdışıyken yerel kaydet + kuyruğa al
    if (!isServerOnline && noteId && isAuthenticated && activeUserId) {
      return applyOfflineNoteUpdate(updatedNote);
    }

    if (!noteId) {
      return updateNoteInternal(updatedNote, options);
    }
    return runSerializedNoteSave(noteId, () => updateNoteInternal(updatedNote, options));
  };

  const updateNoteInternal = async (updatedNote, options = {}) => {
    const { showToast = false, skipRollback = false } = options;
    try {
      // updatedAt zamanını her zaman güncelle
      const now = new Date().toISOString();
      const noteWithUpdatedTime = {
        ...updatedNote,
        updatedAt: now,
        createdAt: normalizeTimestamp(updatedNote.createdAt || updatedNote.created_at || now),
      };

      // Save category to localStorage if it exists
      // 🔴 ROLLBACK İÇİN: Eski state snapshot'ı al
      let oldNote = null;
      let oldSelectedNote = null;
      setNotes(prevNotes => {
        const existingNote = prevNotes.find(note => note.id === noteWithUpdatedTime.id);
        if (existingNote) {
          oldNote = { ...existingNote };
        }
        return prevNotes;
      });
      
      setSelectedNote((current) => {
        if (current?.id === noteWithUpdatedTime.id) {
          oldSelectedNote = { ...current };
        }
        return current;
      });
      
      // 🔴 KRİTİK: Önce API'ye kaydet - başarılı olursa state güncelle
      if (isAuthenticated && activeToken && activeUserId) {
        try {
          // API çağrısı - başarılı olursa DB'den dönen gerçek veri ile state güncelle
          const response = await notesApi.update({
            token: activeToken,
            noteId: noteWithUpdatedTime.id,
            note: {
              title: noteWithUpdatedTime.title || t('common:unnamed'),
              content: noteWithUpdatedTime.content || '',
              titleColor: noteWithUpdatedTime.titleColor || '#ffffff',
              updatedAt: noteWithUpdatedTime.updatedAt,
              categories: noteWithUpdatedTime.categories || [],
            },
          });
          
          // API başarılı - DB'den dönen gerçek notu al
          const savedNote = response?.note;
          if (!savedNote) {
            throw new Error(t('errors.invalidApiResponse'));
          }
          
          // DB'den dönen gerçek verilerle notu formatla; ortak not alanlarını (avatarlar) koru
          const baseNote = {
            id: savedNote.id,
            title: savedNote.title || t('common:unnamed'),
            content: savedNote.content || '',
            titleColor: savedNote.titleColor || savedNote.title_color || '#ffffff',
            createdAt: normalizeTimestamp(savedNote.createdAt || savedNote.created_at),
            updatedAt: normalizeTimestamp(savedNote.updatedAt || savedNote.updated_at || savedNote.createdAt || savedNote.created_at),
          };
          
          // ✅ Başarılı - State'i güncelle; isCollaborative, myRole, collaborators, category korunur
          setNotes(prevNotes => {
            const updatedNotes = prevNotes.map((note) => {
              if (note.id !== baseNote.id) return note;
              return {
                ...baseNote,
                isCollaborative: note.isCollaborative,
                myRole: note.myRole,
                collaborators: Array.isArray(note.collaborators) ? note.collaborators : [],
                categories: noteWithUpdatedTime.categories !== undefined ? noteWithUpdatedTime.categories : note.categories,
              };
            });
            return updatedNotes;
          });
          
          setSelectedNote((current) => {
            if (!current || current.id !== baseNote.id) return current;
            return {
              ...baseNote,
              isCollaborative: current.isCollaborative,
              myRole: current.myRole,
              collaborators: current.collaborators || [],
              categories: noteWithUpdatedTime.categories !== undefined ? noteWithUpdatedTime.categories : current.categories,
            };
          });

          // Başarılı kaydetme bildirimi (sadece gerçek API başarısında, kullanıcı isteğiyle)
          if (showToast && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'success',
                title: t('success.savedToCloud'),
                message: '',
              },
            }));
          }
          // ✅ Başarılı kayıt — taslağı temizle
          if (activeUserId && typeof window !== 'undefined') {
            try {
              window.localStorage.removeItem(`notesplus_draft_${activeUserId}_${noteWithUpdatedTime.id}`);
            } catch (e) { /* ignore */ }
          }
        } catch (error) {
          // 404 = Not sunucuda yok (örn. yeni/offline oluşturulmuş) → upsert (POST) ile oluştur
          const status = error?.status ?? error?.payload?.status;
          const isNotFound =
            Number(status) === 404 ||
            (error?.payload && error.payload.error === 'NOT_FOUND') ||
            (error?.message && String(error.message).includes('Not bulunamadı'));
          if (isNotFound && noteWithUpdatedTime?.id) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[NotesContext] 404 fallback: sunucuda not yok, upsert (POST) deneniyor:', noteWithUpdatedTime.id);
            }
            try {
              const upsertResponse = await notesApi.upsert({
                token: activeToken,
                notes: [{
                  id: noteWithUpdatedTime.id,
                  title: noteWithUpdatedTime.title || t('common:unnamed'),
                  content: noteWithUpdatedTime.content || '',
                  titleColor: noteWithUpdatedTime.titleColor || '#ffffff',
                  createdAt: noteWithUpdatedTime.createdAt,
                  updatedAt: noteWithUpdatedTime.updatedAt,
                }],
              });
              const savedNote = upsertResponse?.note || upsertResponse?.notes?.[0];
              if (!savedNote) {
                throw new Error(t('errors.invalidApiResponse'));
              }
              const baseNote = {
                id: savedNote.id,
                title: savedNote.title || t('common:unnamed'),
                content: savedNote.content || '',
                titleColor: savedNote.titleColor || savedNote.title_color || '#ffffff',
                createdAt: normalizeTimestamp(savedNote.createdAt || savedNote.created_at),
                updatedAt: normalizeTimestamp(savedNote.updatedAt || savedNote.updated_at || savedNote.createdAt || savedNote.created_at),
              };
              setNotes(prevNotes => {
                const updatedNotes = prevNotes.map((note) => {
                  if (note.id !== noteWithUpdatedTime.id) return note;
                  return {
                    ...baseNote,
                    isCollaborative: note.isCollaborative,
                    myRole: note.myRole,
                    collaborators: Array.isArray(note.collaborators) ? note.collaborators : [],
                    categories: noteWithUpdatedTime.categories !== undefined ? noteWithUpdatedTime.categories : note.categories,
                  };
                });
                return updatedNotes;
              });
              if (savedNote.id !== noteWithUpdatedTime.id) {
                setNoteOrder(prev => {
                  const next = prev.map(id => id === noteWithUpdatedTime.id ? savedNote.id : id);
                  persistOrder(next);
                  return next;
                });
              }
              setSelectedNote((current) => {
                if (!current || current.id !== noteWithUpdatedTime.id) return current;
                return {
                  ...baseNote,
                  isCollaborative: current.isCollaborative,
                  myRole: current.myRole,
                  collaborators: current.collaborators || [],
                  categories: noteWithUpdatedTime.categories !== undefined ? noteWithUpdatedTime.categories : current.categories,
                };
              });
              // ✅ Başarılı upsert — taslağı temizle
              if (activeUserId && typeof window !== 'undefined') {
                try {
                  window.localStorage.removeItem(`notesplus_draft_${activeUserId}_${noteWithUpdatedTime.id}`);
                } catch (e) { /* ignore */ }
              }
              return;
            } catch (upsertErr) {
              console.error('Error upserting note (after 404):', upsertErr);
            }
          }

          // Bağlantı yazma sırasında gittiyse: kişisel notu kaybetme, kuyruğa al (rollback yok)
          const existingNote = notesRef.current.find((note) => note.id === noteWithUpdatedTime.id);
          if (
            isNetworkFailure(error) &&
            activeUserId &&
            !isSharedNote(noteWithUpdatedTime) &&
            !isSharedNote(existingNote)
          ) {
            applyOfflineNoteUpdate(noteWithUpdatedTime);
            return;
          }

          // ❌ API başarısız - State'i eski haline döndür (ROLLBACK)
          // Veri kaybını önle: değişiklikleri taslak olarak kaydet
          if (activeUserId && typeof window !== 'undefined') {
            try {
              window.localStorage.setItem(
                `notesplus_draft_${activeUserId}_${noteWithUpdatedTime.id}`,
                JSON.stringify({
                  noteId: noteWithUpdatedTime.id,
                  title: noteWithUpdatedTime.title,
                  content: noteWithUpdatedTime.content,
                  savedAt: new Date().toISOString(),
                })
              );
              window.dispatchEvent(new CustomEvent('notesplus-draft-saved', {
                detail: { noteId: noteWithUpdatedTime.id },
              }));
            } catch (e) { /* ignore */ }
          }
          console.error('Error saving note to API:', error);
          
          if (oldNote && !skipRollback) {
            // Rollback: State'i eski haline döndür
            setNotes(prevNotes => {
              const rolledBackNotes = prevNotes.map((note) =>
                note.id === oldNote.id ? oldNote : note
              );
              return rolledBackNotes;
            });
            
            if (oldSelectedNote) {
              const rollbackId = oldSelectedNote.id;
              setSelectedNote((current) => {
                if (!current || current.id !== rollbackId) return current;
                return oldSelectedNote;
              });
            }
          }
          
          // Kullanıcıya net hata mesajı göster
          const errorMessage = error.isOffline || error.code === 'NETWORK_UNREACHABLE'
            ? t('errors.updateFailedOffline')
            : getApiErrorMessage(error, { fallback: t('errors.updateFailedGeneric') });

          // Toast bildirimi göster (sadece kullanıcı isteğiyle)
          if (showToast && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'error',
                title: t('errors.updateFailedTitle'),
                message: errorMessage,
              },
            }));
          }

          // Hata durumu upstream'e bildirilsin (örnek: save butonunda backup alabilsin)
          throw error;
        }
      } else {
        // Kullanıcı giriş yapmamış - sadece local state'i güncelle (offline mode)
        setNotes(prevNotes => {
          const updatedNotes = prevNotes.map((note) =>
            note.id === noteWithUpdatedTime.id ? noteWithUpdatedTime : note
          );
          return updatedNotes;
        });
        
        setSelectedNote((current) => {
          if (!current || current.id !== noteWithUpdatedTime.id) return current;
          return {
            ...noteWithUpdatedTime,
            isCollaborative: current.isCollaborative,
            myRole: current.myRole,
            collaborators: current.collaborators || [],
            categories: noteWithUpdatedTime.categories !== undefined ? noteWithUpdatedTime.categories : current.categories,
          };
        });
      }
    } catch (error) {
      console.error('Error updating note:', error);

      // Kullanıcıya hata mesajı göster
      const errorMessage = t('errors.updateFailedWithReason', { reason: getApiErrorMessage(error, { fallback: t('errors.unknownError') }) });

      // Toast bildirimi göster
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('errors.updateFailedTitle'),
            message: errorMessage,
          },
        }));
      }
    }
  };


  // Not seç
  const selectNote = (note) => {
    setSelectedNote(note);
  };

  // Not sil
  // 🔴 SENKRON MİMARİ: API başarılı olmadan state değişmeyecek
  // Kural: DB'den silinmeden UI'dan kaldırılmaz
  // NOT: Silme çevrimdışı desteklenmez — mevcut mimaride tombstone/delete kuyruğu yok,
  // sunucu soft-delete'i yalnızca canlı istekle yapılıyor.
  const deleteNote = async (noteId) => {
    if (!assertServerRequiredAction()) {
      return false;
    }
    try {
      // 🔴 KRİTİK: Önce API'den sil - başarılı olursa state'den kaldır
      if (isAuthenticated && activeToken && activeUserId) {
        try {
          // API çağrısı - başarılı olursa state'den kaldır
          await notesApi.delete({ token: activeToken, noteId });
          
          // ✅ API başarılı - Şimdi state'den kaldır
          let remainingOrder = [];
          setNotes(prevNotes => {
            const updatedNotes = prevNotes.filter(note => note.id !== noteId);
            remainingOrder = updatedNotes.map((note) => note.id);
            return updatedNotes;
          });

          persistOrder(remainingOrder);
          setNoteOrder(remainingOrder);
          enqueueReorderSave(remainingOrder);
          
          // Eğer silinen not seçili notsa, seçimi kaldır
          if (selectedNote?.id === noteId) {
            setSelectedNote(null);
          }
          
          // CRITICAL: Notify ChatContext to remove shared note references from messages
          // This prevents "Not Bulunamadı" errors when viewing shared notes in chat
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('notesplus-note-deleted', {
              detail: { noteId }
            }));
            console.log('🔄 [Sync] Notified ChatContext about deleted note:', noteId);
          }
        } catch (error) {
          // MUST_TRANSFER_OWNERSHIP: ortak not sahibi silmek için sahiplik devretmeli – UI modal açar
          if (error?.payload?.error === 'MUST_TRANSFER_OWNERSHIP') {
            throw error;
          }
          // ❌ API başarısız - State'e hiç dokunma
          console.error('Error deleting note from API:', error);
          const errorMessage = error.isOffline || error.code === 'NETWORK_UNREACHABLE'
            ? t('errors.deleteFailedOffline')
            : error.status === 404
            ? t('errors.deleteFailedNotFound')
            : getApiErrorMessage(error, { fallback: t('errors.deleteFailedGeneric') });
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: { type: 'error', title: t('errors.deleteFailedTitle'), message: errorMessage },
            }));
          }
          return;
        }
      } else {
        // Kullanıcı giriş yapmamış - sadece local state'den kaldır (offline mode)
        let remainingOrder = [];
        setNotes(prevNotes => {
          const updatedNotes = prevNotes.filter(note => note.id !== noteId);
          remainingOrder = updatedNotes.map((note) => note.id);
          return updatedNotes;
        });

        persistOrder(remainingOrder);
        setNoteOrder(remainingOrder);
        
        // Eğer silinen not seçili notsa, seçimi kaldır
        if (selectedNote?.id === noteId) {
          setSelectedNote(null);
        }
        
        // CRITICAL: Notify ChatContext to remove shared note references from messages (offline mode)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('notesplus-note-deleted', {
            detail: { noteId }
          }));
          console.log('🔄 [Sync] Notified ChatContext about deleted note (offline):', noteId);
        }
      }
    } catch (error) {
      console.error('Error deleting note:', error);

      // Kullanıcıya hata mesajı göster
      const errorMessage = t('errors.deleteFailedWithReason', { reason: getApiErrorMessage(error, { fallback: t('errors.unknownError') }) });

      // Toast bildirimi göster
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('errors.deleteFailedTitle'),
            message: errorMessage,
          },
        }));
      }
    }
  };

  const clearAllNotes = async () => {
    if (!assertServerRequiredAction()) {
      return;
    }
    try {
      // API'den tüm notları sil
      if (isAuthenticated && activeToken && activeUserId) {
        for (const note of notes) {
          try {
            await notesApi.delete({ token: activeToken, noteId: note.id });
          } catch (error) {
            console.error('Not silme başarısız:', error);
          }
        }
      }

      setNotes([]);
      setNoteOrder([]);
      persistOrder([]);
      enqueueReorderSave([]);
      setSelectedNote(null);
    } catch (error) {
      console.error('Tüm notlar silinirken hata:', error);
      throw error;
    }
  };

  // Create note from shared note (optimistic UI)
  const createNoteFromShared = useCallback(async (noteData) => {
    if (!assertServerRequiredAction()) {
      return null;
    }
    try {
      const { title, content, titleColor } = noteData;
      
      console.log('[NotesContext] createNoteFromShared called:', { title, contentLength: content?.length || 0, titleColor });
      
      // Üyelik limiti kontrolü
      const membershipType = getMembershipType(activeUser);
      const limits = getLimitsForMembership(membershipType);
      const currentNoteCount = notes.length;
      
      if (limits.maxNoteCount !== Infinity && currentNoteCount >= limits.maxNoteCount) {
        const error = new Error(UPGRADE_MESSAGES.noteLimit);
        error.code = 'NOTE_LIMIT_EXCEEDED';
        throw error;
      }

      const now = new Date().toISOString();
      const tempNote = {
        id: `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title: title || t('common:unnamed'),
        content: content || '',
        titleColor: titleColor || '#ffffff',
        createdAt: now,
        updatedAt: now,
      };

      // OPTIMISTIC UI: Add to state immediately
      let nextOrder = [];
      setNotes(prevNotes => {
        const updatedNotes = [...prevNotes, tempNote];
        nextOrder = updatedNotes.map((note) => note.id);
        return updatedNotes;
      });
      persistOrder(nextOrder);
      setNoteOrder(nextOrder);

      console.log('[NotesContext] Optimistic note added:', tempNote);

      // Then save to backend
      if (isAuthenticated && activeToken && activeUserId) {
        try {
          const response = await notesApi.upsert({
            token: activeToken,
            notes: [{
              id: tempNote.id,
              title: tempNote.title,
              content: tempNote.content,
              titleColor: tempNote.titleColor,
              createdAt: tempNote.createdAt,
              updatedAt: tempNote.updatedAt,
            }],
          });

          const savedNote = response?.note || response?.notes?.[0];
          if (savedNote) {
            // Update with real ID from backend
            const realNote = {
              id: savedNote.id,
              title: savedNote.title || t('common:unnamed'),
              content: savedNote.content || '',
              titleColor: savedNote.titleColor || savedNote.title_color || '#ffffff',
              createdAt: normalizeTimestamp(savedNote.createdAt || savedNote.created_at),
              updatedAt: normalizeTimestamp(savedNote.updatedAt || savedNote.updated_at || savedNote.createdAt || savedNote.created_at),
            };

            setNotes(prevNotes => {
              const updated = prevNotes.map(note => 
                note.id === tempNote.id ? realNote : note
              );
              const realOrder = updated.map(note => note.id);
              persistOrder(realOrder);
              setNoteOrder(realOrder);
              return updated;
            });

            console.log('[NotesContext] Note saved to backend:', realNote);
            return realNote;
          }
        } catch (error) {
          console.error('[NotesContext] Backend save error:', error);
          // Remove optimistic note on error
          setNotes(prevNotes => prevNotes.filter(note => note.id !== tempNote.id));
          throw error;
        }
      }

      return tempNote;
    } catch (error) {
      console.error('[NotesContext] createNoteFromShared error:', error);
      throw error;
    }
  }, [notes, activeUser, isAuthenticated, activeToken, activeUserId, persistOrder, setNotes, setNoteOrder, assertServerRequiredAction]);

  // ─── Çevrimdışı kişisel kuyruğun sunucuya gönderilmesi ──────────────────────
  // Sıra: (1) kuyruk sunucuya yazılır → (2) başarılıysa kayıt silinir →
  // (3) tümü başarılıysa sunucu listesi yeniden çekilir → (4) tamamlandı sinyali.
  // Başarısız kayıt kuyrukta kalır; asla "kaydedildi" gibi gösterilmez.
  const flushOfflineQueue = useCallback(async () => {
    if (offlineFlushRef.current) return;
    if (!isAuthenticated || !activeToken || !activeUserId) return;

    const ops = loadOfflineQueue(activeUserId);
    if (!ops.length) {
      setOfflineQueueCount(0);
      setOfflineSyncState('idle');
      dispatchAppEvent('notesplus-offline-sync-complete', {
        synced: 0, failed: 0, skipped: 0, remaining: 0,
      });
      return;
    }

    offlineFlushRef.current = true;
    setOfflineSyncState('syncing');
    setOfflineQueueCount(ops.length);

    let synced = 0;
    let failed = 0;
    let skipped = 0;

    try {
      for (const op of ops) {
        const localNote = notesRef.current.find((note) => note.id === op.noteId);
        // Güvenlik ağı: ortak not bu kuyruktan ASLA sunucuya yazılmaz
        if (isSharedNote(localNote)) {
          skipped += 1;
          console.warn('[NotesContext] Offline kuyruğunda ortak not bulundu, atlandı:', op.noteId);
          continue;
        }

        // Bu arada kullanıcı yazmaya devam ettiyse (bağlantı döndükten sonra) en güncel
        // yerel içeriği gönder — eski kuyruk içeriğinin üzerine yazmasını engeller.
        const opTime = new Date(op.updatedAt || 0).getTime();
        const localTime = new Date(localNote?.updatedAt || 0).getTime();
        const payload = localNote && localTime > opTime ? localNote : op;

        try {
          const response = await notesApi.upsert({
            token: activeToken,
            notes: [{
              id: op.noteId,
              title: payload.title || i18n.t('common:unnamed'),
              content: payload.content || '',
              titleColor: payload.titleColor || '#ffffff',
              createdAt: op.createdAt,
              updatedAt: payload.updatedAt || op.updatedAt,
              ...(payload.categories !== undefined ? { categories: payload.categories } : {}),
            }],
          });

          const savedNote = response?.note || response?.notes?.[0];
          if (!savedNote?.id) {
            throw new Error('INVALID_UPSERT_RESPONSE');
          }

          removeOfflineNoteOp(activeUserId, op.noteId);
          synced += 1;

          // Client UUID gönderildiği için normalde aynı ID döner.
          // Yine de sunucu farklı ID verirse yerel kayıtları eşle (kopya oluşmasın).
          if (String(savedNote.id) !== String(op.noteId)) {
            setNotes((prevNotes) =>
              prevNotes.map((note) => (note.id === op.noteId ? { ...note, id: savedNote.id } : note))
            );
            setSelectedNote((current) =>
              current && current.id === op.noteId ? { ...current, id: savedNote.id } : current
            );
            setNoteOrder((prev) => {
              const next = prev.map((id) => (id === op.noteId ? savedNote.id : id));
              persistOrder(next);
              return next;
            });
          }
        } catch (error) {
          failed += 1;
          markOfflineNoteOpFailure(activeUserId, op.noteId, error?.message);
          console.error('[NotesContext] Offline not senkronizasyonu başarısız:', op.noteId, error);
          // Bağlantı yine koptuysa kalan kayıtları zorlamadan bırak
          if (isNetworkFailure(error)) break;
        }
      }
    } finally {
      offlineFlushRef.current = false;
      const remaining = getOfflineQueueCount(activeUserId);
      setOfflineQueueCount(remaining);
      setOfflineSyncState(failed > 0 ? 'error' : 'idle');

      // Kuyruk tamamen başarıyla gittiyse sunucu listesini source-of-truth olarak yenile
      // Cold-start bootstrap kendi GET'ini yaptığı için skipReload ile çift çekme/stale yarışı önlenir
      if (failed === 0 && synced > 0 && !flushSkipReloadRef.current) {
        dispatchAppEvent('notesplus-reload-notes');
      }
      dispatchAppEvent('notesplus-offline-sync-complete', { synced, failed, skipped, remaining });
    }
  }, [isAuthenticated, activeToken, activeUserId, persistOrder]);

  flushOfflineQueueRef.current = flushOfflineQueue;

  // Bağlantı geri geldiğinde (offline→online) veya bekleyen kayıtla açılışta senkronize et
  const prevServerOnlineRef = useRef(isServerOnline);
  useEffect(() => {
    const wasOnline = prevServerOnlineRef.current;
    prevServerOnlineRef.current = isServerOnline;

    if (!isServerOnline) return;
    if (!isAuthenticated || !activeToken || !activeUserId) return;

    if (!wasOnline || getOfflineQueueCount(activeUserId) > 0) {
      flushOfflineQueue();
    }
    if (!wasOnline || isOrderDirty(activeUserId)) {
      flushPendingNoteOrder();
    }
  }, [isServerOnline, isAuthenticated, activeToken, activeUserId, flushOfflineQueue, flushPendingNoteOrder]);

  // Kullanıcı değişince kuyruk sayacını tazele
  useEffect(() => {
    setOfflineQueueCount(getOfflineQueueCount(activeUserId));
  }, [activeUserId]);

  // Sync başarısız kaldıysa: pencere öne gelince sessizce tekrar dene (reload yok, loop yok)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!isServerOnline || offlineSyncState !== 'error') return undefined;

    const retry = () => {
      if (document.visibilityState === 'hidden') return;
      if (getOfflineQueueCount(activeUserId) > 0) flushOfflineQueue();
      if (isOrderDirty(activeUserId)) flushPendingNoteOrder();
    };

    window.addEventListener('focus', retry);
    document.addEventListener('visibilitychange', retry);
    return () => {
      window.removeEventListener('focus', retry);
      document.removeEventListener('visibilitychange', retry);
    };
  }, [isServerOnline, offlineSyncState, activeUserId, flushOfflineQueue, flushPendingNoteOrder]);

  // Üyelik bilgilerini hesapla
  const membershipType = useMemo(() => getMembershipType(activeUser), [activeUser]);
  const membershipLimits = useMemo(() => getLimitsForMembership(membershipType), [membershipType]);
  
  // API'den notları yeniden yükle (import sonrası anlık senkron için)
  const reloadNotes = useCallback(async () => {
    if (!isAuthenticated || !activeToken || !activeUserId) return;
    setIsLoading(true);
    try {
      const [response, orderFetch] = await Promise.all([
        notesApi.list({ token: activeToken }),
        fetchCloudNoteOrder(activeToken),
      ]);
      const apiNotes = response?.notes || [];
      const formattedNotes = apiNotes.map(note => ({
        id: note.id,
        title: note.title || t('common:unnamed'),
        content: note.content || '',
        titleColor: note.titleColor || note.title_color || '#ffffff',
        createdAt: normalizeTimestamp(note.createdAt || note.created_at),
        updatedAt: normalizeTimestamp(note.updatedAt || note.updated_at || note.createdAt || note.created_at),
      }));
      const remainingOps = loadOfflineQueue(activeUserId);
      const mergedNotes = applyOfflineQueueOverlay(formattedNotes, remainingOps);
      const orderedNotes = await applyNotesWithResolvedOrder(mergedNotes, orderFetch);
      setNotes(orderedNotes);
      setOfflineQueueCount(remainingOps.length);
    } catch (error) {
      console.error('❌ [NotesContext] reloadNotes failed:', error);
      const queuedOps = loadOfflineQueue(activeUserId);
      const mergedNotes = applyOfflineQueueOverlay(notesRef.current || [], queuedOps);
      if (mergedNotes.length) {
        const storedOrder = loadStoredOrder();
        setNotes(syncNotesWithOrder(mergedNotes, storedOrder));
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, activeToken, activeUserId, loadStoredOrder, syncNotesWithOrder, applyNotesWithResolvedOrder, t]);

  // Not limiti bilgileri
  const noteCount = notes.length;
  const maxNoteCount = membershipLimits.maxNoteCount;
  const canCreateMoreNotes = maxNoteCount === Infinity || noteCount < maxNoteCount;
  const remainingNotes = maxNoteCount === Infinity ? null : Math.max(0, maxNoteCount - noteCount);

  // Kategori yönetimi: tüm notlarda kategori adını/rengini güncelle (rename) veya temizle (delete)
  const updateCategoryInNotes = useCallback((oldName, newName, newColor) => {
    const remap = (cats) => {
      const arr = cats || [];
      if (!arr.some((c) => c.name === oldName)) return arr;
      if (newName) return arr.map((c) => c.name === oldName ? { name: newName, color: newColor } : c);
      return arr.filter((c) => c.name !== oldName);
    };
    setNotes((prev) =>
      prev.map((n) => {
        const newCats = remap(n.categories);
        if (newCats === (n.categories || [])) return n;
        return { ...n, categories: newCats };
      })
    );
    setSelectedNote((prev) => {
      if (!prev) return prev;
      const newCats = remap(prev.categories);
      if (newCats === (prev.categories || [])) return prev;
      return { ...prev, categories: newCats };
    });
  }, []);

  const value = {
    notes,
    noteOrder,
    selectedNote,
    isLoading,
    createNote,
    updateNote,
    duplicateNote,
    reorderNotes,
    moveNoteUp,
    moveNoteDown,
    selectNote,
    deleteNote,
    clearAllNotes,
    importNotes,
    reloadNotes,
    createNoteFromShared,
    updateCategoryInNotes,
    // Üyelik ve limit bilgileri
    noteCount,
    maxNoteCount,
    canCreateMoreNotes,
    remainingNotes,
    membershipType,
    // Çevrimdışı kişisel not senkronizasyonu
    offlineQueueCount,
    offlineSyncState,
    flushOfflineQueue,
    isSharedNote,
  };

  return <NotesContext.Provider value={value}>{children}</NotesContext.Provider>;
};

export const useNotes = () => {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
};
